/**
 * Push Notification Hook
 * Handles service worker registration, subscription management, and preferences
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = window.location.origin;

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [preferences, setPreferences] = useState(null);
  const [registration, setRegistration] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 
                      'PushManager' in window && 
                      'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and check subscription status
  useEffect(() => {
    const initPush = async () => {
      if (!isSupported) {
        setIsLoading(false);
        return;
      }

      try {
        // Register service worker
        const reg = await navigator.serviceWorker.register('/sw-push.js');
        setRegistration(reg);
        
        // Check if already subscribed
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
        
        // Fetch user preferences
        await fetchPreferences();
      } catch (error) {
        console.error('Error initializing push notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initPush();
  }, [isSupported]);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/push/preferences`);
      setPreferences(response.data.preferences);
      setIsSubscribed(response.data.has_subscription);
    } catch (error) {
      console.error('Error fetching push preferences:', error);
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported || !registration) {
      throw new Error('Push notifications not supported');
    }

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Get VAPID public key from server
      const keyResponse = await axios.get(`${API_URL}/api/push/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.publicKey;
      
      // Extract the raw key from PEM format if needed
      let applicationServerKey;
      if (vapidPublicKey.includes('BEGIN PUBLIC KEY')) {
        // It's in PEM format, need to extract and convert
        const pemContent = vapidPublicKey
          .replace('-----BEGIN PUBLIC KEY-----', '')
          .replace('-----END PUBLIC KEY-----', '')
          .replace(/\n/g, '');
        applicationServerKey = urlBase64ToUint8Array(pemContent);
      } else {
        applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      // Send subscription to server
      await axios.post(`${API_URL}/api/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        },
        expirationTime: subscription.expirationTime
      });

      setIsSubscribed(true);
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      throw error;
    }
  }, [isSupported, registration]);

  const unsubscribe = useCallback(async () => {
    if (!registration) return;

    try {
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();
        
        // Remove from server
        await axios.delete(`${API_URL}/api/push/unsubscribe`, {
          params: { endpoint: subscription.endpoint }
        });
      }

      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  }, [registration]);

  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      await axios.put(`${API_URL}/api/push/preferences`, newPreferences);
      setPreferences(prev => ({ ...prev, ...newPreferences }));
      return true;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }, []);

  const sendTestNotification = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/push/test`);
      return response.data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences,
    sendTestNotification,
    fetchPreferences
  };
};

export default usePushNotifications;
